import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ZyAnimatorNodeView from './zyanimator-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyAnimator: {
            insertZyAnimator: (options?: InsertZyAnimatorOptions) => ReturnType;
        };
    }
}

export interface InsertZyAnimatorOptions {
    caption?: string;
    animXMLToLoad?: string;
    height?: string;
    width?: string;
    alt_text?: string;
    image_ids?: string[];
    maImageNodeIds?: Record<string, string>;
}

export const ZyAnimatorNodeHeadless = Node.create({
    name: 'zyAnimator',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-animator-id'),
                renderHTML: attributes => ({
                    'data-animator-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            animXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-anim-xml') || '',
                renderHTML: attributes => ({
                    'data-anim-xml': attributes.animXMLToLoad,
                }),
            },
            height: {
                default: '350px',
                parseHTML: element => element.getAttribute('data-height') || '350px',
                renderHTML: attributes => ({
                    'data-height': attributes.height,
                }),
            },
            width: {
                default: '600px',
                parseHTML: element => element.getAttribute('data-width') || '600px',
                renderHTML: attributes => ({
                    'data-width': attributes.width,
                }),
            },
            alt_text: {
                default: '',
                parseHTML: element => element.getAttribute('data-alt-text') || '',
                renderHTML: attributes => ({
                    'data-alt-text': attributes.alt_text,
                }),
            },
            image_ids: {
                default: [],
                parseHTML: element => {
                    const ids = element.getAttribute('data-image-ids');
                    return ids ? ids.split(',') : [];
                },
                renderHTML: attributes => ({
                    'data-image-ids': attributes.image_ids?.join(',') || '',
                }),
            },
            maImageNodeIds: {
                default: {},
                parseHTML: element => {
                    try {
                        return JSON.parse(element.getAttribute('data-ma-image-node-ids') || '{}');
                    } catch {
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-ma-image-node-ids': JSON.stringify(attributes.maImageNodeIds || {}),
                }),
            },
            animator_version: {
                default: '2',
                parseHTML: element => element.getAttribute('data-animator-version') || '2',
                renderHTML: attributes => ({
                    'data-animator-version': attributes.animator_version,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="zy-animator"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'zy-animator',
                class: 'zy-animator-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertZyAnimator:
                (options: InsertZyAnimatorOptions = {}) =>
                ({ commands }) => {
                    const emptyAnimation = `<zyAnimator caption="" height="350px" width="600px" made-with="2" colorPalette="aug23" selectedInstr="none" selectedObj="none" numObjsEverCreated="0" loadOnDemand="false" additionalLatexSize="2">
          <zyObjects></zyObjects><zyInstructions>
          <zyInstruction instrType="step1"><text></text></zyInstruction>

          <zyInstruction instrType="startStep"><text></text></zyInstruction>
          </zyInstructions>
          <zyAltDescription></zyAltDescription>
          </zyAnimator>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            animXMLToLoad: options.animXMLToLoad || emptyAnimation,
                            height: options.height || '350px',
                            width: options.width || '600px',
                            alt_text: options.alt_text || '',
                            image_ids: options.image_ids || [],
                            maImageNodeIds: options.maImageNodeIds || {},
                        },
                    });
                },
        };
    },
});

export const ZyAnimatorNode = ZyAnimatorNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyAnimatorNodeView);
    },
});

export default ZyAnimatorNode;
